/* Toggle — matches Figma Toggle / Switch (node 1107:946). Two sizes
 * (sm/lg). Each track + thumb pair is sized exactly per the Figma
 * spec:
 *
 *   sm → track 28×16, thumb 12×12, inset 2px (unselected left:2,
 *                                             selected left:14)
 *   lg → track 34×20, thumb 16×16, inset 2px (unselected left:2,
 *                                             selected left:16)
 *
 * DOM structure:
 *   <label> wrapper (flex row: [input | track > thumb | label-text])
 *     <input type=checkbox role=switch>  ← sr-only, native interaction
 *     <span .track>                       ← pill background
 *       <span .thumb>                     ← absolute-positioned circle
 *     </span>
 *     <span .label>Text</span> (optional)
 *   </label>
 *
 * All visual state is CSS: sibling selectors on `.input` drive the
 * .track background, and descendant selectors on `.track .thumb`
 * move the thumb + paint the focus ring. No JS mirror. */

.wrapper {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-8);
  padding: 2px;
  cursor: pointer;
  user-select: none;
}

.wrapper:has(.input:disabled) {
  cursor: not-allowed;
}

/* Input captures clicks; track sits on top but is pointer-events:none
 * so clicks fall through. Position absolute + opacity 0 hides it
 * visually while keeping focus / keyboard / form semantics intact. */
.input {
  position: absolute;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: inherit;
}

/* ─── Size-specific track + input dimensions ─────────────────── */

.size-sm .input {
  width: 28px;
  height: 16px;
}

.size-sm .track {
  width: 28px;
  height: 16px;
}

.size-sm .thumb {
  width: 12px;
  height: 12px;
}

/* Selected thumb position: track width - thumb width - inset.
 *   sm: 28 - 12 - 2 = 14px */
.size-sm .input:checked + .track .thumb {
  left: 14px;
}

.size-lg .input {
  width: 34px;
  height: 20px;
}

.size-lg .track {
  width: 34px;
  height: 20px;
}

.size-lg .thumb {
  width: 16px;
  height: 16px;
}

/*   lg: 34 - 16 - 2 = 16px */
.size-lg .input:checked + .track .thumb {
  left: 16px;
}

/* ─── Track (pill background) ───────────────────────────────── */

/* --radius-round is 200px, which on a 16/20px-height pill produces
 * the correct fully-rounded shape (any radius ≥ half the height
 * gives a capsule). Same token used by Chip. */
.track {
  border-radius: var(--radius-round);
  background: var(--bg-tertiary);
  pointer-events: none;
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  transition: background var(--motion-duration-fast);
}

/* Hover on the wrapper dims the track one step — light mode paints
 * neutral-300 (#d4d4d4 ≈ --button-outline-default), dark paints
 * neutral-500 (#737373, no Figma alias). Only applies when unselected
 * and not disabled — selected states keep the accent tint. */
.wrapper:hover .input:not(:disabled):not(:checked) + .track {
  background: var(--button-outline-default);
}

[data-theme="dark"] .wrapper:hover .input:not(:disabled):not(:checked) + .track {
  /* #737373 — neutral-500, no Figma alias. Flagged in design-asks. */
  background: #737373;
}

/* Selected (on) — teal track. Same literals as Checkbox accent
 * pending a shared `--accent-brand` token. */
.input:checked + .track {
  background: #0d9488;
}

[data-theme="dark"] .input:checked + .track {
  background: #5eead4;
}

/* Disabled fill — neutral muted in both modes. Thumb position still
 * reflects `checked`, so the user can see the state even disabled. */
.input:disabled + .track {
  background: var(--icon-muted);
}

[data-theme="dark"] .input:disabled + .track {
  background: var(--bg-tertiary);
}

/* ─── Thumb (white pill) ─────────────────────────────────────── */

/* --radius-round keeps the thumb a circle at every size (any radius
 * ≥ half the side gives a circle — same reasoning as --radius-round
 * on the track). */
.thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  background: #ffffff;
  border-radius: var(--radius-round);
  transition:
    left var(--motion-duration-fast),
    box-shadow var(--motion-duration-fast);
}

/* Focus-visible: 4px outer halo around the thumb. Figma specifies
 * different rgba stops than Checkbox — darker (20% alpha) in light,
 * lighter (30% alpha) in dark. The 4px thickness is also distinct
 * from Checkbox's 2px ring. Flagged in design-asks alongside the
 * Checkbox focus-ring gap (both candidates for a shared
 * `--focus-ring-*` token set). */
.input:focus-visible + .track .thumb {
  box-shadow: 0 0 0 4px rgba(64, 64, 64, 0.2);
}

[data-theme="dark"] .input:focus-visible + .track .thumb {
  box-shadow: 0 0 0 4px rgba(163, 163, 163, 0.3);
}

/* Focus ring swaps to teal tint when selected. Same 20% teal alpha
 * Checkbox uses for its selected focus ring. */
.input:focus-visible:checked + .track .thumb {
  box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.2);
}

/* ─── Label text ─────────────────────────────────────────────── */

.label {
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-primary);
}

.wrapper:has(.input:disabled) .label {
  color: var(--text-tertiary);
}
